import { Fragment } from 'react';
import { Link } from 'react-router-dom';
import { Disclosure, Transition, Menu } from '@headlessui/react';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { googleLogout } from '@react-oauth/google';
import { useDispatch, useSelector } from 'react-redux';
import { logoutUserAction } from '@/js/reduxstore/actions/authActions';
import LanguageSelector from '../LanguageSelector';
import { useTranslation } from 'react-i18next';
import CustomDemoButton from '../Buttons/CustomDemoButton';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function Navbar() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const user = useSelector((state) => state?.authReducer?.userData);

  // Default navigation items (user.role is null)
  const defaultNavigation = [
    { name: 'Book a demo', to: 'https://cal.com/ssjaiswal' },
    { name: 'About us', to: '/about' },
    { name: 'Login', to: '/signin' },
  ];

  // // Buyer navigation items (user.role is 'buyer')
  // const buyerNavigation = [
  //   {
  //     name: 'My Dashboard',
  //     to: `/${user?.route ? user?.route : ''}dashboard?tab=recent_activity`,
  //   },
  //   { name: 'Messages', to: '/user/messages' },
  //   { name: 'Contact Us', to: '/contact' },
  //   { name: 'About Us', to: '/about' },
  // ];

  // // Supplier navigation items (user.role is 'supplier')
  // const supplierNavigation = [
  //   {
  //     name: 'Supplier Dashboard',
  //     to: `/${user?.route ? user?.route : ''}supplier/dashboard`,
  //   },
  //   { name: 'Orders', to: '/supplier/orders' },
  //   { name: 'Account Settings', to: '/supplier/settings' },
  // ];

  // const navigation =
  //   user?.role === 'supplier'
  //     ? supplierNavigation
  //     : user?.role === 'buyer'
  //     ? buyerNavigation
  //     : defaultNavigation;

  const navigation = defaultNavigation;

  // Conditional class for supplier navbar (no shadow and rounded corners)
  const navbarClasses = classNames(
    'text-[15px] font-robo sm:px-12 bg-white',
    user?.role === 'supplier'
      ? 'rounded-none shadow-none'
      : 'rounded-b-3xl shadow-sm',
  );

  return (
    <Disclosure as="nav" className={navbarClasses}>
      {({ open }) => (
        <>
          <div className="py-3">
            <div className="relative flex flex-col sm:flex-row items-center justify-between">
              {/* Mobile menu button*/}
              <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
                <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-black hover:bg-[#8D8D8D] hover:text-black focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="block h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="block h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>
              </div>

              {/* Logo Section */}
              <div className="flex flex-row items-center justify-center space-x-2">
                <Link
                  to={`/${user?.route ? user?.route : ''}`}
                  className="w-auto h-auto"
                >
                  <img
                    className="h-10 w-10"
                    src="/blackLogo.svg"
                    alt="Your Company"
                  />
                </Link>
                <Link
                  to="/"
                  className="text-[22px] text-center font-semibold sm:text-left"
                >
                  MakerSharks
                </Link>
              </div>

              {/* Desktop Menu Items */}
              <div className="hidden sm:flex sm:justify-center sm:items-center">
                <div className="flex items-center align-center font-medium">
                  {navigation.map((item) =>
                    item.name === 'Book a demo' ? (
                      // Special button for "Book a Demo"
                      <CustomDemoButton key={item.name} to={item.to}>
                        {item.name}
                      </CustomDemoButton>
                    ) : (
                      <Link
                        key={item.name}
                        to={item.to}
                        className={
                          item.name === 'Login'
                            ? 'rounded-md text-center mx-4 pt-1 pb-1 pr-4 pl-4 bg-white text-darkPurple border border-darkPurple hover:bg-darkPurple hover:text-white focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white transition ease-in duration-300'
                            : 'rounded-lg text-center mx-2 pt-2 pb-2 pr-3 pl-3 hover:bg-purple-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white transition ease-in-out duration-500'
                        }
                        aria-current={item.current ? 'page' : undefined}
                      >
                        {item.name}
                      </Link>
                    ),
                  )}
                </div>

                {/* language dropdown */}
                {/* <LanguageSelector type={"dropdown"}/> */}
              </div>

              {/* Profile dropdown */}
              <div className="absolute inset-y-0 right-0 flex pr-2 sm:hidden sm:inset-auto sm:pr-0">
                <Menu as="div" className="relative">
                  <div>
                    {user ? (
                      <div className="flex flex-row items-center hover:cursor-pointer ">
                        <div className=" px-4 text-[18px] text-center mx-1 text-opacity-70">
                          Hi {user.given_name}!
                        </div>
                        <Menu.Button className="relative flex rounded-full bg-[#D9D9D9] text-sm focus:outline-none hover:ring-2 hover:ring-white hover:ring-offset-2 hover:ring-offset-black sm:mr-10">
                          <span className="absolute -inset-1.5" />
                          <span className="sr-only">Open user menu</span>
                          <img
                            className="h-10 w-10 rounded-full"
                            src={user?.picture}
                            alt="user"
                          />
                        </Menu.Button>
                      </div>
                    ) : (
                      <div>{/* <GoogleAuthButton /> */}</div>
                    )}
                  </div>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute right-0 z-10 mt-2 w-48 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="/user"
                            className={classNames(
                              active ? 'bg-blue-400' : '',
                              'block px-4 py-2 text-sm',
                            )}
                          >
                            Your Profile
                          </Link>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            // onClick={() =>
                            //   dispatch(
                            //     setUserRoleAction(
                            //       supplierDashboardData,
                            //       'supplier'
                            //     )
                            //   )
                            // }
                            className={classNames(
                              active ? 'bg-blue-400' : '',
                              'block px-4 py-2 text-sm w-full text-left',
                            )}
                          >
                            Switch to Supplier
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            // onClick={() =>
                            //   dispatch(
                            //     setUserRoleAction(
                            //       makerhsharksDashboardData,
                            //       'makersharks'
                            //     )
                            //   )
                            // }
                            className={classNames(
                              active ? 'bg-blue-400' : '',
                              'block px-4 py-2 text-sm w-full text-left',
                            )}
                          >
                            Switch to Makersharks
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <Link
                            to="#"
                            className={classNames(
                              active ? 'bg-blue-400' : '',
                              'block px-4 py-2 text-sm ',
                            )}
                            onClick={() => {
                              window.location.reload();
                              localStorage.removeItem('accessToken');
                              localStorage.removeItem('refreshToken');
                              googleLogout();
                              dispatch(logoutUserAction());
                            }}
                          >
                            Sign out
                          </Link>
                        )}
                      </Menu.Item>
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            </div>
          </div>

          {/* Mobile Menu Panel */}
          <Disclosure.Panel className="sm:hidden">
            <div className="space-y-1 px-4 pb-3 pt-2">
              {navigation.map((item) => (
                <Disclosure.Button
                  key={item.name}
                  as={Link}
                  to={item.to}
                  className={classNames(
                    'text-black',
                    'block rounded-full px-3 py-2 text-base',
                  )}
                  aria-current={item.current ? 'page' : undefined}
                >
                  {item.name}
                </Disclosure.Button>
              ))}
            </div>
          </Disclosure.Panel>
        </>
      )}
    </Disclosure>
  );
}
